import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { NotAMemberOfClubScreen } from "@/features/auth/NotAMemberOfClubScreen";
import { getPublicNavCapabilities } from "@/features/navigation/navCapabilities";
import { useAuthStore } from "@/stores/auth";
import type { MePayload } from "@/lib/types";

/** Multi-club: true when the user is signed in, on a club subdomain, but
 * holds no membership in THAT club. The backend already refuses their
 * data; the UI shows a club switcher instead of redirecting into the app. */
export function isNonMemberOfHostClub(me: MePayload | null): boolean {
  const clubId = me?.current_club?.id;
  if (!me?.user || !clubId) return false;
  const memberships = me.club_memberships ?? [];
  return !memberships.some((m) => m.club_id === clubId);
}

/**
 * `/hub` — role-based redirect (M12a).
 *
 * Priority order:
 *   - global admin / env-admin / club_admin / team_admin → `/admin`
 *     (the legacy `uploader` token was retired here in role-deprecation PR 3)
 *   - coach / assistant_coach                          → `/coach`
 *   - other signed-in user with no team membership     → `/register`
 *   - guardian / player (feedback access)              → `/feedback`
 *   - viewer-only member (team:read, no feedback)      → `/teams`
 *   - anonymous                                        → `/login?next=/hub`
 *
 * (A club_admin holds NO team membership by design, so it must be recognized
 * in the `/admin` tier above — otherwise the "no team membership → /register"
 * fallback would wrongly bounce them to request team access.)
 *
 * The Hub button on the marketing landing reads "tktic hub" — the
 * role-based dispatcher, not a fixed coach-review shortcut. Admins
 * land in the admin console, coaches land in the coach workspace,
 * guardians / players go to `/feedback`, and viewer-only members go
 * to the read-only `/teams` index.
 *
 * `useRef` guards the redirect so TanStack Router's parent-stays-mounted
 * behavior across an intra-effect navigate doesn't fire the redirect a
 * second time (same load-bearing pattern as M9a / M9b auth gates).
 */
export function HubRedirect() {
  const navigate = useNavigate();
  const meQuery = useMe();
  const authMe = useAuthStore((s) => s.me);
  const hydrating = useAuthStore((s) => s.hydrating);
  const redirected = useRef(false);

  const nonMember = isNonMemberOfHostClub(authMe);

  useEffect(() => {
    if (redirected.current) return;
    if (hydrating || meQuery.isLoading) return;

    const user = authMe?.user;
    if (!user) {
      redirected.current = true;
      void navigate({
        to: "/login",
        search: { next: "/hub" },
        replace: true,
      });
      return;
    }

    // Signed in but not a member of THIS club's subdomain — render the
    // switcher (below) instead of dispatching into the app.
    if (isNonMemberOfHostClub(authMe)) return;

    const landing = getHubLanding(authMe) ?? "/teams";

    redirected.current = true;
    void navigate({ to: landing, replace: true });
  }, [authMe, hydrating, meQuery.isLoading, navigate]);

  if (nonMember && !hydrating && !meQuery.isLoading) {
    return <NotAMemberOfClubScreen />;
  }

  return (
    <main
      data-testid="hub-redirect"
      className="flex min-h-[40vh] items-center justify-center bg-canvas text-ink"
    >
      <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
    </main>
  );
}

export type HubLandingPath =
  | "/admin"
  | "/coach"
  | "/feedback"
  | "/register"
  | "/teams";

export function getHubLanding(
  me: MePayload | null | undefined,
): HubLandingPath | null {
  const user = me?.user;
  if (!user) return null;

  const roles = user.roles ?? [];
  const memberships = me?.memberships ?? [];
  const isGlobalAdmin = !!user.is_global_admin || roles.includes("admin");
  // A club admin runs the club from /admin. They hold NO team memberships by
  // design (club_admin is a separate tier), so they must be recognized here —
  // otherwise the `memberships.length === 0` fallback below bounces them to
  // /register to "request team access", which makes no sense for a club admin.
  const isClubAdmin = !!me?.is_club_admin;
  const isTeamAdmin = memberships.some((m) => m.role === "team_admin");
  const isCoach = memberships.some(
    (m) => m.role === "coach" || m.role === "assistant_coach",
  );

  if (isGlobalAdmin || isClubAdmin || isTeamAdmin) return "/admin";
  if (isCoach) return "/coach";
  if (memberships.length === 0) return "/register";
  // Split feedback-capable members (player / guardian) from viewer-only ones
  // using the SAME predicate the /feedback route gate uses
  // (navCapabilities.canOpenFeedback) — so the two surfaces can never disagree
  // on who /feedback accepts. A viewer holds only team:read; sending it to
  // /feedback bounced it back to /hub in an infinite loop. Land viewer-only
  // members on the read-only public /teams index, which never bounces.
  if (getPublicNavCapabilities(me).canOpenFeedback) return "/feedback";
  return "/teams";
}
